/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { jsx } from '@compiled/react';

import { AiSuggestionsEntryPointValue } from '@atlaskit/editor-common/analytics/ai-suggestions-entry-points';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../../pm-plugins/main';
import {
	type ContentReviewerAgent,
	useContentReviewerAgentResolver,
} from '../../hooks/useContentReviewerAgentResolver';
import { FOLLOW_UP_CARD_ANCHOR_NAMES } from '../../utils/followUpCard';
import { AnchoredCard } from '../AnchoredCard';
import { FollowUpCard } from './FollowUpCard';

/**
 * Renders the follow-up card in the position of the suggestion card it replaces.
 *
 * Position comes from the same CSS-anchor machinery as the suggestion card: on removal the
 * plugin keeps anchor spans alive at the removed suggestion's target (see
 * `calculateFollowUpCardDecorations`), so the card tracks that spot through resizes, scrolling
 * and reflow rather than freezing at measured coordinates.
 */
export const FollowUpCardContentComponent = ({
	api,
	view,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	view?: EditorView;
}): JSX.Element | null => {
	const cardRef = useRef<HTMLDivElement>(null);

	const { followUpCard, isHidden } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions', 'editorViewMode'],
		(state) => ({
			followUpCard: state?.aiSuggestionsState?.followUpCard,
			// View mode is a hard override: never show the suggestions UI while read-only.
			isHidden:
				(state?.aiSuggestionsState?.isHidden ?? false) ||
				state?.editorViewModeState?.mode === 'view',
		}),
	);

	// Resolved only while the card is on screen, so nothing is provisioned or fetched for a card
	// that isn't shown. Until it lands the card falls back to the Rovo mark. The agent store
	// caches the lookup, so re-opening doesn't refetch.
	const [agent, setAgent] = useState<ContentReviewerAgent | undefined>();
	const { resolveAgent } = useContentReviewerAgentResolver({ api });
	const isCardShown = Boolean(followUpCard) && !isHidden;

	useEffect(() => {
		if (!isCardShown || agent || !fg('content_reviewer_agent')) {
			return;
		}
		let isActive = true;
		const abortController = new AbortController();

		void resolveAgent({ signal: abortController.signal }).then((resolved) => {
			if (isActive && resolved) {
				setAgent(resolved);
			}
		});

		return () => {
			isActive = false;
			abortController.abort();
		};
	}, [agent, isCardShown, resolveAgent]);

	const onDismiss = useCallback(() => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, { action: 'DISMISS_FOLLOW_UP_CARD' });
			return tr;
		});
	}, [api]);

	const onReview = useCallback(() => {
		api?.aiSuggestions?.actions.setThinkingState('thinking', {
			entryPoint: AiSuggestionsEntryPointValue.FOLLOW_UP_CARD,
		});
		// The thinking bar takes over from here.
		onDismiss();
	}, [api, onDismiss]);

	if (!followUpCard || isHidden) {
		return null;
	}

	return (
		<AnchoredCard
			// Remounts per suggestion, so a card replacing another one fades in rather than
			// silently swapping content.
			key={followUpCard.suggestionId}
			anchorNames={FOLLOW_UP_CARD_ANCHOR_NAMES}
			cardRef={cardRef}
			targetId={followUpCard.suggestionId}
			view={view}
		>
			<FollowUpCard agent={agent} ref={cardRef} onDismiss={onDismiss} onReview={onReview} />
		</AnchoredCard>
	);
};
